import { BodySecondary } from '@liveauctioneers/hammer-ui-core/text';
import { FormattedMessage } from '@liveauctioneers/hammer-ui-core/intl';
import {
    getItemSummaryBuyNowAvailable,
    getItemSummaryIsPassed,
    getItemSummaryIsSold,
    getItemSummarySellerId,
    getItemSummarySellerName,
    getItemSummaryStartTime,
} from '@/redux/modules/item/summary/itemSummary.selectors';
import { getSellerUrl } from '@/utils/urls';
import { useAppSelector } from '@/redux/hooks';
import { WeakRouteLink } from '@/components/RouteLinks/RouteLinks';
import ItemCardShortDate from '@/components/GridItemCard/Card/components/ItemCardShortDate/ItemCardShortDate';
import ms from 'ms';
import styled from 'styled-components';

export type CardInfoProps = {
    isCatalogOnline: boolean;
    isLive: boolean;
    isMobile: boolean;
    isPending: boolean;
    itemId: number;
};

const CardInfo = ({ isCatalogOnline, isLive, isMobile, isPending, itemId }: CardInfoProps) => {
    const isSold = useAppSelector((state) => getItemSummaryIsSold(state, itemId));
    const isPassed = useAppSelector((state) => getItemSummaryIsPassed(state, itemId));
    const isBuyNow = useAppSelector((state) => getItemSummaryBuyNowAvailable(state, itemId));
    const saleStartTs = useAppSelector((state) => getItemSummaryStartTime(state, itemId));
    const sellerId = useAppSelector((state) => getItemSummarySellerId(state, itemId));
    const sellerName = useAppSelector((state) => getItemSummarySellerName(state, itemId));

    const biddingWithin7Days = Date.now() > saleStartTs * 1000 - ms('7d');
    const showDateWithoutYear = isCatalogOnline && !biddingWithin7Days;

    return (
        <PlacedItemInfo data-testid="myItemsCardInfo">
            <StyledDateInfo>
                {isBuyNow ? (
                    <BodySecondary color="secondary">
                        <FormattedMessage id="buy_now" />
                    </BodySecondary>
                ) : (
                    <ItemCardShortDate
                        allowBidding
                        itemId={itemId}
                        showDateWithoutYear={showDateWithoutYear}
                        showOnlyYear={isSold || isPassed || isPending}
                    />
                )}
            </StyledDateInfo>
            {!isLive && !isMobile && (
                <StyledAuctionHouseName
                    data-testid="cardInfoAuctionHouseName"
                    to={getSellerUrl(sellerId, sellerName)}
                >
                    <BodySecondary color="secondary">{sellerName}</BodySecondary>
                </StyledAuctionHouseName>
            )}
        </PlacedItemInfo>
    );
};

export default CardInfo;

const PlacedItemInfo = styled.section`
    grid-area: iteminfo;
    display: flex;
    justify-content: space-between;
    min-width: 0;
`;

const StyledDateInfo = styled.span`
    line-height: 18px;
    white-space: nowrap;

    @media (max-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidth}) {
        width: 100%;
    }
    @media (max-width: 375px) {
        width: unset;
    }
`;

const StyledAuctionHouseName = styled(WeakRouteLink)`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    line-height: 18px;
    text-align: right;
    text-decoration: none;
    max-width: 50%;
    &:hover > span {
        color: ${({ theme }) => theme.colors.blue100};
        text-decoration: underline;
    }
`;
